/** Byte time-domain samples sit at 128 in silence and swing either side of it. */
const SILENT_SAMPLE = 128;

/**
 * Mean absolute amplitude is small even for a raised voice, so it is amplified
 * before being clamped. The gain matches the legacy responder's audio worklet,
 * which is what keeps a spoken answer reading the same on both waveforms.
 */
const MICROPHONE_LEVEL_GAIN = 5;

const MICROPHONE_LEVEL_ATTACK = 0.6;
const MICROPHONE_LEVEL_RELEASE = 0.2;

/**
 * Coarse enough that a live microphone republishes a handful of times a second
 * instead of once per sample — every published value re-renders the session.
 */
const MICROPHONE_LEVEL_STEPS = 20;

/** Normalised 0–1 loudness of one frame of byte time-domain samples. */
export function toMicrophoneLevel(samples: Uint8Array): number {
  if (samples.length === 0) {
    return 0;
  }

  const total = samples.reduce(
    (sum, sample) => sum + Math.abs(sample - SILENT_SAMPLE),
    0
  );

  const level =
    (total / samples.length / SILENT_SAMPLE) * MICROPHONE_LEVEL_GAIN;

  return Math.min(1, level);
}

/**
 * Asymmetric on purpose, the same way the shared waveform's envelope is: a
 * syllable has to reach the pill at once, while the gap between two words must
 * not blink it back to silence.
 */
export function smoothMicrophoneLevel(previous: number, next: number): number {
  const weight =
    next > previous ? MICROPHONE_LEVEL_ATTACK : MICROPHONE_LEVEL_RELEASE;

  return previous + (next - previous) * weight;
}

export function quantizeMicrophoneLevel(level: number): number {
  return Math.round(level * MICROPHONE_LEVEL_STEPS) / MICROPHONE_LEVEL_STEPS;
}
